// P8 returns and corrections (RET-C-04): return invoices on paid invoices, adjustments of unpaid invoices, receipt
// reversals. Reads come from api_v1 views; writes go through the operations (architecture §3.3).
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { supabase } from './supabase';

export const RETURNS = ['returns'] as const;

export interface ReturnLine {
  id: number;
  product: string;
  sku: string | null;
  pieces: number;
  unit_net: number;
  value: number;
  damaged: boolean;
}
export interface CustomerReturn {
  id: string;
  number: string;
  sale_id: string;
  sale_number: string;
  sale_date: string;
  round_id: string;
  round_number: string;
  truck_id: string;
  truck_code: string;
  seller_id: string;
  seller_name: string | null;
  customer_id: string;
  customer: string;
  reason: string;
  refund: number;
  business_date: string;
  at: string;
  has_damaged: boolean;
  lines: ReturnLine[];
  /** Owner only (GEN-15); null for a Seller. */
  cost: number | null;
}
export interface InvoiceAdjustment {
  id: string;
  sale_id: string;
  sale_number: string;
  round_id: string;
  round_number: string;
  truck_id: string;
  seller_id: string;
  seller_name: string | null;
  customer_id: string;
  customer: string;
  reason: string;
  total_before: number;
  total_after: number;
  business_date: string;
  at: string;
  lines: { product: string; pieces: number; value: number }[];
}
export interface Returnable {
  sale_id: string;
  sale_line_id: number;
  product_id: string;
  product: string;
  sku: string | null;
  sold: number;
  pieces_left: number;
  unit_net: number;
  returnable_until: string;
}
export interface RoundReturns {
  round_id: string;
  refunded: number;
  returns: number;
  adjustments: number;
  adjusted: number;
}

function rows<T>(res: { data: unknown; error: { message: string } | null }): T[] {
  if (res.error) throw new Refusal('SERVER_ERROR');
  return (res.data ?? []) as T[];
}
const num = (v: unknown) => (v === null || v === undefined ? 0 : Number(v));

export async function loadReturns(filter: { round?: string | undefined } = {}): Promise<CustomerReturn[]> {
  let q = supabase.from('v_returns').select('*').order('at', { ascending: false }).limit(200);
  if (filter.round) q = q.eq('round_id', filter.round);
  return rows<CustomerReturn>(await q).map((r) => ({
    ...r,
    refund: num(r.refund),
    cost: r.cost === null ? null : num(r.cost),
  }));
}
export async function loadReturn(id: string): Promise<CustomerReturn | null> {
  return (await loadReturnsWhere('id', id))[0] ?? null;
}
async function loadReturnsWhere(column: string, value: string): Promise<CustomerReturn[]> {
  return rows<CustomerReturn>(await supabase.from('v_returns').select('*').eq(column, value)).map((r) => ({
    ...r,
    refund: num(r.refund),
    cost: r.cost === null ? null : num(r.cost),
  }));
}
export async function loadAdjustments(filter: { round?: string | undefined } = {}): Promise<InvoiceAdjustment[]> {
  let q = supabase.from('v_invoice_adjustments').select('*').order('at', { ascending: false }).limit(200);
  if (filter.round) q = q.eq('round_id', filter.round);
  return rows<InvoiceAdjustment>(await q).map((a) => ({
    ...a,
    total_before: num(a.total_before),
    total_after: num(a.total_after),
  }));
}
/** What each line of a sale still holds, for the return and adjustment sheets. */
export async function loadReturnable(sale: string): Promise<Returnable[]> {
  return rows<Returnable>(
    await supabase.from('v_sale_returnable').select('*').eq('sale_id', sale).order('sale_line_id'),
  ).map((l) => ({ ...l, unit_net: num(l.unit_net) }));
}
export async function loadRoundReturns(round: string): Promise<RoundReturns | null> {
  const list = rows<RoundReturns>(await supabase.from('v_round_returns').select('*').eq('round_id', round));
  const r = list[0];
  return r ? { ...r, refunded: num(r.refunded), adjusted: num(r.adjusted) } : null;
}

export interface LineChoice {
  line: number;
  pieces: number;
  damaged?: boolean;
}
export const postCustomerReturn = (round: string, sale: string, lines: LineChoice[], reason: string) =>
  sendCommand<{ id: string; number: string; refund: number }>('post_customer_return', {
    p_round: round,
    p_sale: sale,
    p_lines: lines,
    p_reason: reason,
  });
export const adjustUnpaidInvoice = (round: string, sale: string, lines: LineChoice[], reason: string) =>
  sendCommand<{ id: string; number: string; total_before: number; total_after: number }>('adjust_unpaid_invoice', {
    p_round: round,
    p_sale: sale,
    p_lines: lines,
    p_reason: reason,
  });
export const reverseCollection = (collection: string, reason: string) =>
  sendCommand<{ id: string; number: string; amount: number; source: 'round' | 'garage' }>('reverse_collection', {
    p_collection: collection,
    p_reason: reason,
  });
